import React, { useEffect, useRef, useState } from "react";
import "./HowItWorks.css";

/* ============================================================
   CONTENT  (yahin se edit karo)
   ============================================================ */
const HEAD = {
  badge: "HOW IT WORKS",
  title: "Get Started in Minutes",
  subtitle: "No complicated setup required. Start managing your shop digitally today.",
};

const STEPS = [
  {
    icon: "box",
    title: "Add Your Products",
    text: "Upload via CSV, scan barcodes, or snap photos using camera integration to build your catalog instantly.",
    tags: ["CSV Upload", "Barcode Scan", "Camera Photos"],
  },
  {
    icon: "receipt",
    title: "Create Fast Bills",
    text: "Use the Quick Sales POS to generate GST invoices, print receipts, or send digital copies.",
    tags: ["GST Invoices", "Print Receipts", "Digital Copies"],
  },
  {
    icon: "chart",
    title: "Track & Grow",
    text: "Monitor live stock levels, check sales trends, and collect pending payments via WhatsApp reminders.",
    tags: ["Live Stock", "Sales Trends", "WhatsApp Reminders"],
  },
];

const AUTO_MS = 4800; // auto-advance time

/* ============================================================
   ICONS
   ============================================================ */
const ICONS = {
  box: (
    <>
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
      <path d="m3.3 7 8.7 5 8.7-5M12 22V12" />
    </>
  ),
  receipt: (
    <>
      <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
      <path d="M14.5 8.5h-4a2 2 0 0 0 0 4h3a2 2 0 0 1 0 4H9M12 6.5v2M12 16.5v2" />
    </>
  ),
  chart: (
    <>
      <path d="M3 3v16a2 2 0 0 0 2 2h16" />
      <path d="m19 9-5 5-4-4-3 3" />
    </>
  ),
};

const Icon = ({ name, size = 28 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {ICONS[name]}
  </svg>
);

/* ============================================================
   Mini animated illustrations (shown in the ring centre)
   ============================================================ */
const Scene1 = () => (
  <svg viewBox="0 0 120 120" className="hw__scene" aria-hidden="true">
    {/* box */}
    <path className="hw-s1-left" d="M26 64 60 77v31L26 95Z" />
    <path className="hw-s1-right" d="M60 77 94 64v31l-34 13Z" />
    <path className="hw-s1-top" d="M26 64 60 51l34 13-34 13Z" />
    {/* barcode on the box */}
    <g className="hw-s1-code">
      <rect x="68" y="80" width="2.5" height="14" />
      <rect x="73" y="78" width="4" height="14" />
      <rect x="80" y="76" width="2" height="14" />
      <rect x="85" y="73" width="4" height="14" />
    </g>
    <rect className="hw-s1-beam" x="66" y="70" width="26" height="2.5" rx="1.2" />
    {/* items dropping in */}
    <rect className="hw-s1-item hw-s1-item--a" x="32" y="2" width="17" height="17" rx="4" />
    <rect className="hw-s1-item hw-s1-item--b" x="51" y="2" width="17" height="17" rx="4" />
    <rect className="hw-s1-item hw-s1-item--c" x="70" y="2" width="17" height="17" rx="4" />
  </svg>
);

const Scene2 = () => (
  <svg viewBox="0 0 120 120" className="hw__scene" aria-hidden="true">
    <path
      className="hw-s2-paper"
      d="M30 10h60v100l-7.5-5-7.5 5-7.5-5-7.5 5-7.5-5-7.5 5-7.5-5-7.5 5Z"
    />
    <rect className="hw-s2-line" style={{ "--d": "0.2s" }} x="40" y="24" width="30" height="5" rx="2.5" />
    <rect className="hw-s2-line" style={{ "--d": "0.5s" }} x="40" y="38" width="40" height="4" rx="2" />
    <rect className="hw-s2-line" style={{ "--d": "0.8s" }} x="40" y="50" width="36" height="4" rx="2" />
    <rect className="hw-s2-line" style={{ "--d": "1.1s" }} x="40" y="62" width="40" height="4" rx="2" />
    <rect className="hw-s2-total" x="40" y="76" width="40" height="10" rx="3" />
    <g className="hw-s2-stamp">
      <circle cx="86" cy="88" r="15" />
      <path d="m79 88 5 5 9-10" />
    </g>
  </svg>
);

const Scene3 = () => (
  <svg viewBox="0 0 120 120" className="hw__scene" aria-hidden="true">
    <path className="hw-s3-axis" d="M16 14v92h92" />
    <rect className="hw-s3-bar" style={{ "--d": "0.1s" }} x="28" y="70" width="14" height="36" rx="3" />
    <rect className="hw-s3-bar" style={{ "--d": "0.3s" }} x="50" y="54" width="14" height="52" rx="3" />
    <rect className="hw-s3-bar" style={{ "--d": "0.5s" }} x="72" y="42" width="14" height="64" rx="3" />
    <rect className="hw-s3-bar" style={{ "--d": "0.7s" }} x="94" y="26" width="14" height="80" rx="3" />
    <path className="hw-s3-trend" d="M24 62 46 46 68 38 100 14" pathLength="1" />
    <circle className="hw-s3-dot" cx="100" cy="14" r="5" />
  </svg>
);
const SCENES = [Scene1, Scene2, Scene3];

/* scroll-in hook */
const useInView = (threshold = 0.2) => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (!("IntersectionObserver" in window)) {
      setSeen(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, seen];
};

/* ring geometry */
const ANGLES = [-90, 30, 150];
const RADIUS = 42; // % of ring size
const GAP = 14; // degrees kept free around each node
const pt = (deg) => {
  const a = (deg * Math.PI) / 180;
  return [50 + RADIUS * Math.cos(a), 50 + RADIUS * Math.sin(a)];
};
const arcPath = (i) => {
  const from = ANGLES[i] + GAP;
  const to = (i === 2 ? ANGLES[0] + 360 : ANGLES[i + 1]) - GAP;
  const [x1, y1] = pt(from);
  const [x2, y2] = pt(to);
  return `M${x1.toFixed(2)} ${y1.toFixed(2)} A${RADIUS} ${RADIUS} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
};

/* ============================================================
   SECTION
   ============================================================ */
const HowItWorks = () => {
  const [ref, seen] = useInView();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!seen || paused) return undefined;
    const id = setTimeout(() => setActive((a) => (a + 1) % STEPS.length), AUTO_MS);
    return () => clearTimeout(id);
  }, [seen, paused, active]);

  const Scene = SCENES[active];

  return (
    <section
      ref={ref}
      className={`hw ${seen ? "hw--in" : ""}`}
      id="how-it-works"
      style={{ "--auto": `${AUTO_MS}ms` }}
    >
      <span className="hw__dots" aria-hidden="true" />
      <div className="hw__container">
        {/* ---------- heading ---------- */}
        <header className="hw__head">
          <span className="hw__pill hw__r">{HEAD.badge}</span>
          <h2 className="hw__title hw__r" style={{ "--i": 1 }}>
            {HEAD.title}
          </h2>
          <span className="hw__underline" />
          <p className="hw__subtitle hw__r" style={{ "--i": 2 }}>
            {HEAD.subtitle}
          </p>
        </header>

        <div
          className="hw__stage"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* ---------- rotating ring ---------- */}
          <div className="hw__ring hw__r hw__r--left" style={{ "--i": 3 }}>
            <span className="hw__halo" aria-hidden="true" />
            <span className="hw__ticks" aria-hidden="true" />
            <span className="hw__disc" aria-hidden="true" />
            <span className="hw__disc hw__disc--2" aria-hidden="true" />

            {/* spark orbiting the ring */}
            <span className="hw__orbit" aria-hidden="true">
              <i />
            </span>

            {/* centre: animated scene */}
            <div className="hw__core">
              <div className="hw__core-body" key={active}>
                <Scene />
                <small>STEP {active + 1}</small>
              </div>
            </div>

            {/* rotating layer: progress arcs + nodes */}
            <div className="hw__nodes">
              <svg className="hw__arcs" viewBox="0 0 100 100" aria-hidden="true">
                <defs>
                  <linearGradient id="hwGrad" x1="0" y1="0" x2="100" y2="100" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#4fc3d0" />
                    <stop offset="1" stopColor="#12305e" />
                  </linearGradient>
                </defs>
                {STEPS.map((s, i) => (
                  <g key={s.title}>
                    <path d={arcPath(i)} className="hw__arc-base" />
                    {i < active && <path d={arcPath(i)} className="hw__arc-done" />}
                    {i === active && (
                      <path
                        d={arcPath(i)}
                        className="hw__arc-live"
                        pathLength="1"
                        style={{
                          animationDuration: `${AUTO_MS}ms`,
                          animationPlayState: paused ? "paused" : "running",
                        }}
                      />
                    )}
                  </g>
                ))}
              </svg>

              {STEPS.map((s, i) => {
                const [x, y] = pt(ANGLES[i]);
                return (
                  <button
                    type="button"
                    key={s.title}
                    className={`hw__node ${i === active ? "is-active" : ""} ${
                      i < active ? "is-done" : ""
                    }`}
                    style={{ left: `${x}%`, top: `${y}%` }}
                    onClick={() => setActive(i)}
                    aria-label={`Step ${i + 1}: ${s.title}`}
                  >
                    <span className="hw__node-in">
                      <b>{i + 1}</b>
                      <Icon name={s.icon} size={22} />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ---------- steps list ---------- */}
          <ol className="hw__steps">
            {STEPS.map((s, i) => (
              <li
                key={s.title}
                className={`hw__step hw__r hw__r--right ${i === active ? "is-active" : ""} ${
                  i < active ? "is-done" : ""
                }`}
                style={{ "--i": i + 3 }}
              >
                <button type="button" onClick={() => setActive(i)}>
                  <span className="hw__step-num">{String(i + 1).padStart(2, "0")}</span>
                  <span className="hw__step-ico">
                    <Icon name={s.icon} size={26} />
                  </span>
                  <span className="hw__step-body">
                    <small>STEP {i + 1}</small>
                    <strong>{s.title}</strong>
                    <em className="hw__desc">{s.text}</em>
                    <span className="hw__step-more">
                      <span className="hw__tags">
                        {s.tags.map((t) => (
                          <i key={t}>{t}</i>
                        ))}
                      </span>
                    </span>
                  </span>
                  <span className="hw__step-bar" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;